import { Link } from '@tanstack/react-router';
import { ShieldCheck, FileCheck2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { SURFACES } from '@/lib/gql/__generated__/surfaces';
import { useCanEnterSurface } from '@/lib/platform/surfaces';
import { useSession } from '@/lib/platform/hooks';
import { leaveAfterSignOut } from '@/lib/platform/routing';
import type { ReactNode } from 'react';

const navigation = [
  { path: '/filing-setup', title: 'Filing Setup', surface: SURFACES['filing-setup'] },
  { path: '/source-intake', title: 'Source Intake', surface: SURFACES['source-intake'] },
  { path: '/filing-facts', title: 'Filing Facts', surface: SURFACES['filing-facts'] },
  { path: '/return-review', title: 'Return Review', surface: SURFACES['return-review'] },
  { path: '/filing-submission', title: 'Filing Submission', surface: SURFACES['filing-submission'] },
] as const;
export function AppChrome({
  children,
  title,
  description,
}: {
  children: ReactNode;
  title: string;
  description: string;
}) {
  const session = useSession();
  const allowed = [
    useCanEnterSurface(SURFACES['filing-setup']),
    useCanEnterSurface(SURFACES['source-intake']),
    useCanEnterSurface(SURFACES['filing-facts']),
    useCanEnterSurface(SURFACES['return-review']),
    useCanEnterSurface(SURFACES['filing-submission']),
  ];
  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="border-b border-border bg-card">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-4 px-5 py-4">
          <Link
            to="/filing-setup"
            className="flex items-center gap-2 font-heading text-lg font-semibold tracking-tight text-primary hover:opacity-80 focus-visible:outline-2 focus-visible:outline-ring"
          >
            <ShieldCheck className="size-6" />
            TrustFile
            <span className="ml-2 border-l border-border pl-3 text-xs font-normal uppercase tracking-widest text-muted-foreground">
              Firm workspace
            </span>
          </Link>
          <div className="flex items-center gap-3 text-xs text-muted-foreground">
            <span>{session.user?.name || session.user?.email || 'Firm member'}</span>
            <Button variant="outline" size="sm" onClick={() => void leaveAfterSignOut()} data-jr-key="account-sign-out">
              Sign out
            </Button>
          </div>
        </div>
      </header>
      <div className="mx-auto grid max-w-7xl grid-cols-1 md:grid-cols-[220px_1fr]">
        <nav
          aria-label="Filing workflow"
          className="flex gap-1 overflow-auto border-b border-border bg-sidebar px-4 py-3 md:min-h-[calc(100vh-73px)] md:flex-col md:border-r md:border-b-0 md:py-7"
        >
          <p className="hidden px-3 pb-3 text-[11px] font-semibold uppercase tracking-widest text-muted-foreground md:block">
            Workflow
          </p>
          {navigation
            .filter((_, index) => allowed[index])
            .map((item) => (
              <Link
                key={item.path}
                to={item.path}
                data-jr-key={`${item.path.slice(1)}-nav-link`}
                activeProps={{ 'aria-current': 'page', className: 'bg-primary text-primary-foreground' }}
                className="whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium text-sidebar-foreground transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-2 focus-visible:outline-ring"
              >
                {item.title}
              </Link>
            ))}
          <div className="mt-auto hidden border-t border-sidebar-border px-3 pt-6 text-xs leading-relaxed text-muted-foreground md:block">
            <FileCheck2 className="mb-2 size-5 text-primary" />
            Tax-year work, from source to receipt.
          </div>
        </nav>
        <main className="min-w-0 px-5 py-8 md:px-10 md:py-10">
          <div className="mb-8 border-b border-border pb-6">
            <p className="mb-2 text-xs font-semibold uppercase tracking-widest text-primary">
              TrustFile / Tax-year filing
            </p>
            <h1 className="font-heading text-3xl font-semibold tracking-tight">{title}</h1>
            <p className="mt-2 max-w-2xl text-sm text-muted-foreground">{description}</p>
          </div>
          {children}
        </main>
      </div>
    </div>
  );
}
